import {
  Component,
  forwardRef,
  Inject,
  Input,
  OnInit
} from '@angular/core';

import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { NgbActiveModal }     from '@ng-bootstrap/ng-bootstrap';
import { TranslateService }   from '@ngx-translate/core';

import { BookingsAppointmentTypeModel } from './typings/bookings.appointment-type';
import { BookingsRequestModel }         from './typings/bookings.request';
import { BookingsService }              from './bookings.service';
import { CodeTypeModel }                from '../core/models/code-type';
import { CompanyService }               from '../companies/company.service';
import { CustomerModel }                from '../customers/models/customer';
import { DateParserService }            from '../core/services/date-parser.service';
import { MessagesService }              from '../messages/messages.service';
import { SessionService }               from '../sessions/session.service';

@Component({
  selector   : 'wor-booking-request',
  template: require('./bookings.request.component.html')
})
export class BookingsRequestComponent implements OnInit {
  regoLabel : string = this.sessionService.countrySettings().rego_label;
  selectedCustomerId = ''

  @BlockUI('bookingRequestBlock') block : NgBlockUI;

  @Input() appointmentTypes: Array<BookingsAppointmentTypeModel>  = [];
  @Input() bodyTypes       : Array<CodeTypeModel> = [];
  @Input() contactTypes    : Array<CodeTypeModel> = [];
  @Input() request         : BookingsRequestModel;

  constructor (
    public  activeModal      : NgbActiveModal,
    @Inject(forwardRef(() => BookingsService))
    private bookingsService  : BookingsService,
    public  companyService   : CompanyService,
    private dateParserService: DateParserService,
    private messagesService  : MessagesService,
    private sessionService   : SessionService,
    private translateService : TranslateService
  ) {}

  ngOnInit () : void {
    const company = this.sessionService.company();

    if ( company.company_variable_fields.plate_number_name ) {
      this.regoLabel = company.company_variable_fields.plate_number_name;
    }

    this.request.start_date = this.dateParserService.format(this.request.local_start_time, this.dateParserService.dateFormat);
    this.request.start_time = this.dateParserService.format(this.request.local_start_time, this.dateParserService.timeFormat);
  }

  approve () : void {
    const isNewCustomer = this.isNewCustomer();

    this.updateLocalStartTime();

    this.activeModal.close({
      createNewCustomer : isNewCustomer,
      message           : this.translateService.instant('JOB_STATUS_TYPES.BOOKED'),
      request           : this.request,
      selectedCustomerId: isNewCustomer ? null : this.selectedCustomerId,
      skipApproval      : false
    });
  }

  delete () : void {
    this.bookingsService.deleteRequest(this.request)
    .then(() => this.activeModal.close({
        deleted     : true,
        message     : this.translateService.instant('JS_SPACE.MESSAGES.DELETED.DELETED'),
        request     : this.request,
        skipApproval: true
      }))
    .catch(error => this.messagesService.error(error));
  }

  formatterFn = ( mechanic : any ) => {
    return typeof mechanic === 'object'
      ? mechanic.display_name
      : mechanic;
  }

  getCandidateLabel ( candidate : CustomerModel ) : string {
    let name = `${ candidate.first_name || '' } ${ candidate.last_name || '' }`.trim();

    name = name || candidate.company_name || `#${ candidate.id }`;

    const details : Array<string> = [];

    if (candidate.company_name && name !== candidate.company_name) {
      details.push(candidate.company_name);
    }

    if (candidate.mobile) {
      details.push(candidate.mobile);
    }

    if (candidate.email) {
      details.push(candidate.email);
    }

    const prefix = candidate.vehicle_owner
      ? `★ ${ this.translateService.instant('VEHICLE_SPACE.VEHICLE_OWNER') } — `
      : '';

    return `${ prefix }${ details.length
      ? `${ name } — ${ details.join(' • ') }`
      : name }`;
  }

  hasCandidates () : boolean {
    return !this.request.customer_id
      && Array.isArray(this.request.candidate_customers)
      && this.request.candidate_customers.length >= 1;
  }

  hasMultipleCandidates () : boolean {
    return !this.request.customer_id
      && Array.isArray(this.request.candidate_customers)
      && this.request.candidate_customers.length >= 2;
  }

  hasMultiplePlateMatches () : boolean {
    return !!this.request.multiple_plate_matches;
  }

  hasNameMismatch () : boolean {
    return !!this.request.name_mismatch;
  }

  hasSingleCandidate () : boolean {
    return !this.request.customer_id
      && Array.isArray(this.request.candidate_customers)
      && this.request.candidate_customers.length === 1;
  }

  isNewCustomer () : boolean {
    return this.hasCandidates() && !this.selectedCustomerId;
  }

  onMechanicChange ( mechanic : any ) : void {
    this.request.mechanic_id           = mechanic.id;
    this.request.mechanic_display_name = mechanic.display_name;

    this.verifyAvailability();
  }

  searchFn = ( query : string ) => {
    return this.bookingsService.getMechanics(query);
  }

  update () : void {
    this.updateLocalStartTime();

    this.activeModal.close({
      message     : this.translateService.instant('JS_SPACE.MESSAGES.UPDATED.UPDATED'),
      request     : this.request,
      skipApproval: true
    });
  }

  updateLocalStartTime () : void {
    this.request.local_start_time = this.bookingsService.formatLocalStartTime(this.request);
  }

  verifyAvailability () : void {
    if ( !!this.request.start_date && !!this.request.start_time && !!this.request.mechanic_display_name ) {
      this.bookingsService.validateBookingTime(this.request)
      .then(() => {
        //
      })
      .catch(error => this.messagesService.error(error));
    }
  }

  viewDiary () : void {
    this.bookingsService.viewDiary(this.request);
  }
}
